//
// Utility functions to work with AEC-specific model data. These are stored in a separate JSON file AECModelData.json.
// Currently only available for Revit files.
//

const namespace = AutodeskNamespace('Autodesk.AEC');

export const TopViewViewportTypes = ['FloorPlan', 'CeilingPlan', 'EngineeringPlan', 'AreaPlan'];

// Computes a Matrix4 that maps the 'from' rectangle to the 'to' rectangle in xy.
const remapRectangle = (
    xMinFrom, yMinFrom, xMaxFrom, yMaxFrom,
    xMinTo,   yMinTo,   xMaxTo,   yMaxTo
) => {
    let scaleX = (xMaxTo - xMinTo) / (xMaxFrom - xMinFrom);
    let scaleY = (yMaxTo - yMinTo) / (yMaxFrom - yMinFrom);

    // Note that the translation component of the matrix works on the scaled values.
    // The scaling alone would map xMinFrom to scaleX * xMinFrom. We want
    // to map it to xMinTo instead. (analog for y)
    let offsetX = xMinTo - scaleX * xMinFrom;
    let offsetY = yMinTo - scaleY * yMinFrom;

    // Create Matrix4 that applies both
    let matrix = new THREE.Matrix4();
    matrix.elements[0] = scaleX;
    matrix.elements[5] = scaleY;
    // Set the Z scale to be the same as X scale. This is similar to what we do at the end of get3DTo2DModelSheetTransform.
    // In general, for a transform that comes from Revit the scale should be uniform in all directions, otherwise the sheet
    // would be distorted.
    matrix.elements[10] = scaleX;
    matrix.elements[12] = offsetX;
    matrix.elements[13] = offsetY;
    return matrix;
};

const getFeetToSheetUnits = (sheetUnitScale) => {
    const FeetToMeter       = 0.3048;
    const MeterToSheetUnits = 1.0 / sheetUnitScale;
    return FeetToMeter * MeterToSheetUnits;
};

// Returns the 2D bbox of a viewport on a sheet.
//
// @param {Object} vp             - Viewport data as read from AECModelData
// @param {number} sheetUnitScale - UnitScale of the sheet containing the viewport, e.g., 0.0254 if the sheet unit is inches. See Model.getUnitScale().
// @returns {THREE.Box2|null}
const getViewportBounds = (vp, sheetUnitScale) => {

    // viewport region in foot as array (6 floats)
    const values = vp.geometryViewportRegion;
    if (!values) {
        return null;
    }

    // Convert from foot to sheet units (usually inches)
    const scale = getFeetToSheetUnits(sheetUnitScale);

    // The viewport region returned by Revit is incorrect. It's enlarged by an offset of 0.01 ft.
    // We remove that offset before scaling.
    // See https://thebuildingcoder.typepad.com/blog/2010/09/view-location-on-sheet.html (search for 0.01 in that page)
    const boundsCorrection = 0.01;

    let res = new THREE.Box2();
    res.min.x = (values[0] + boundsCorrection) * scale;
    res.min.y = (values[1] + boundsCorrection) * scale;
    res.max.x = (values[3] - boundsCorrection) * scale;
    res.max.y = (values[4] - boundsCorrection) * scale;
    return res;
};

// Handle available view rotation modes in Revit
// If a view was rotated in Revit, this swapped/inverted some viewport axes. We must revert this modification when transforming back to world coords.
// This function returns a Matrix4 that performs this rotation on an input vector p in normalized viewport coords (in [0,1])
//
//  @param {number} viewRotationType - as read from AECModelData. Enum value in {0,1,2}. see Revit API doc:
//                                     http://www.revitapidocs.com/2018/abd81744-b44a-e578-cb62-3cc40a334acc.htm
//  @returns {THREE.Vector2/3} Copy of vpCoords with swapped/inverted axes according to viewport rotation
const getInverseViewportRotation = (viewRotationType) => {

    const matrix = new THREE.Matrix4();
    switch(viewRotationType) {
        // No rotation => done
        case 0: return matrix;

        // Revit view was rotated by 90 degrees clockwise => Rotate by 90 degrees ccw
        case 1: {
            // Note that a rotation would actually mean result.x = -y. But, we just revert the effect the rotation effect on the
            // viewport axes here. I.e., input and output are in [0,1]. For this, swapping an axis means taking 1.0-value, not just the negative.
            // Therefore, the desired effect of the matrix is:
            //
            //  (x, y) => (1-y, x)
            //
            // Note the memory layout of THREE matrices is column-major.

            // out.x = (1-y)
            matrix.elements[0]  =  0;  //  0 * in.x
            matrix.elements[4]  = -1;  // -1 * in.y
            matrix.elements[12] =  1;  // + 1

            // out.y = x
            matrix.elements[1] = 1; // 1 * in.x
            matrix.elements[5] = 0; // 0 * in.y

            break;
        }

        // Revit view was rotated 90 degrees counterclockwise => Rotate by 90 degrees cw
        case 2: {
            // Desired transform here is:
            //
            // (x, y) => (y, 1-x)

            // out.x = y
            matrix.elements[0] = 0; // 0 * in.x
            matrix.elements[4] = 1; // 1 * in.y

            // out.y = (1-x)
            matrix.elements[1]  = -1; //    -in.x
            matrix.elements[5]  =  0; // 0 * in.y
            matrix.elements[13] =  1; // + 1

            break;
        }
        default: console.warn('Unexpected enum value for view rotation: ', viewRotationType);
    }
    return matrix;
};

const get3DTo2DModelSheetTransform = (vp, sheetUnitScale) => {
    // Only if AECModelData has been produced using latest RevitAPI changes,
    // we get the 3DTo2D matrix directly. Otherwise return null.
    if (!vp.modelToSheetTransform) {
        return null;
    }

    const values = vp.modelToSheetTransform;
    const matrix = Autodesk.Viewing.BubbleNode.readMatrixFromArray12(values);

    // apply post-scaling from feet to sheet-units
    // Note that using multiplyScalar() here is only similar, but not the same: E.g., it would also multiply component 15
    // which may cause weird side-effects when multiplying with other matrices.
    // The Z value of the scale is set to feetToSheetUnits as well so that getMaxScaleOnAxis
    // returns a meaningful result (used to be 1, which would skew the results)
    const feetToSheetUnits = getFeetToSheetUnits(sheetUnitScale);
    const scaleTf = new THREE.Matrix4().makeScale(feetToSheetUnits, feetToSheetUnits, feetToSheetUnits);
    matrix.multiplyMatrices(scaleTf, matrix);

    return matrix;
};

const compute2Dto3DTransform = (vp, sheetUnitScale) => {
    // Viewport bbox on sheet (Box2)
    const sheetRegion = getViewportBounds(vp, sheetUnitScale);

    // SectionBox (Box3 + Matrix4): Oriented box in 3D world coords. Represents the volume
    // that is mapped to the sheet.
    const sectionBox          = vp.sectionBox;
    const sectionBoxTransform = Autodesk.Viewing.BubbleNode.readMatrixFromArray12(sectionBox.transform);

    // Compute matrix that maps from sheet xy coords to normalized viewport coords in [0,1]^2
    let sheetToViewport = remapRectangle(
        sheetRegion.min.x, sheetRegion.min.y, sheetRegion.max.x, sheetRegion.max.y, // ...from viewport rectangle in sheet coordinates
        0, 0, 1, 1                                                                  // ...to [0,1]
    );

    // Consider Revit viewport rotation. Result keeps within viewport coords ([0,1]^2)
    let vpRotationInv = getInverseViewportRotation(vp.viewportRotation);

    // Compute matrix that maps from sheet xy coords to normalized viewport coords in [0,1]^2
    let viewportToSectionBox = remapRectangle(
        0, 0, 1, 1,                                                                 // ...from normalized viewport coords
        sectionBox.min.x,  sectionBox.min.y,  sectionBox.max.x,  sectionBox.max.y   // ...to the xy-extent of the untransformed SectionBox
    );

    // Finally, apply the sectionBoxTransform to obtain world coords. Note that matrix products must be done in reverse order.
    const matrix = sectionBoxTransform
        .multiply(viewportToSectionBox)
        .multiply(vpRotationInv)
        .multiply(sheetToViewport);

    return matrix;
};

// Compute matrix to convert 2D sheet coords within the given viewport to 3D model coordinates.
//
// @param {Object} vp             - Viewport data as read from AECModelData
// @param {number} sheetUnitScale - UnitScale of the sheet containing the viewport, e.g., 0.0254 if the sheet unit is inches. See Model.getUnitScale().
//
// @returns {THREE.Matrix4}
//
// Note: Not all viewports provide A 2D/3D transform is not provided by all This feature is only available for a subset of  viewports.
const get2DTo3DMatrix = (vp, sheetUnitScale) => {
    const matrix = get3DTo2DModelSheetTransform(vp, sheetUnitScale);

    if (matrix) {
        return matrix.invert();
    }

    return compute2Dto3DTransform(vp, sheetUnitScale);
};

// Compute matrix to convert 3D model coords to 2D sheet coords within a given viewport. See get2Dto3DMatrix().
const get3DTo2DMatrix = (vp, sheetUnitScale) => {
    let matrix = get3DTo2DModelSheetTransform(vp, sheetUnitScale);

    if (matrix) {
        return matrix;
    }

    matrix = compute2Dto3DTransform(vp, sheetUnitScale);
    return matrix.invert();
};

// Find Sheet BubbleNode containing the given viewport.
//
//  @params {av.BubbleNode} root - root node of a manifest in which we search for sheets
//  @params {Object}        vp   - Viewport as obtained from AecModelData.json file (aecData.viewports)
//  @returns {av.BubbleNode[]|undefined} BubbleNode of the sheet containing the viewport (if any).
const findSheetForViewport = (root, vp) => {
    let sheetNodes = root.search(Autodesk.Viewing.BubbleNode.SHEET_NODE);
    let linkedByVp = (node) => node.guid() === vp.sheetGuid;
    return sheetNodes.find(linkedByVp);
};

// Find all viewports on a given sheet. The sheet can be specified as model or BubbleNode.
// Note that not all viewports support 2D/3D transform.
//
//  @param {av.BubbleNode|av.Model} sheet
const findViewportsOnSheet = (sheet) => {

    // get aecModelData from model or node
    const isNode    = sheet instanceof Autodesk.Viewing.BubbleNode;
    const sheetNode = (isNode ? sheet : sheet.getDocumentNode());
    const aec       = sheetNode.getAecModelData();
    if (!aec || !aec.viewports) {
        return [];
    }

    // Find viewports linking the sheet guid
    return aec.viewports.filter(vp => vp.sheetGuid === sheetNode.guid());
};

// Given a sheet and a point on that sheet, this function returns the viewport containing it.
// If multiple viewports overlap, we choose the one where (x,y) is "most inside", i.e.,
// where the distance from the viewport edges is largest.
//
//  @param {av.Model}         sheet
//  @param {Vector2}          point            - Point in sheet world-coords.
//  @param {function(Object)} [viewportFilter] - Option to exclude certain viewports
//
//  @returns {Object|undefined}
const findViewportAtPoint = (sheet, point, viewportFilter = undefined) => {

    console.warn('Please use ViewportExtension.findViewportAtPoint instead.');

    // Find sheets linking this sheet
    let viewports = findViewportsOnSheet(sheet);

    // Apply option filter
    if (viewportFilter) {
        viewports = viewports.filter(viewportFilter);
    }

    // Couple each viewport with viewport bounds
    viewports = viewports.map(vp => {
        return {
            viewport: vp,
            bounds:   getViewportBounds(vp, sheet.getUnitScale())
        };
    });

    // Reduce to viewports containing the given point
    const containsPoint = vp => {
        return vp.bounds && vp.bounds.containsPoint(point);
    };
    viewports = viewports.filter(containsPoint);

    // Point may be outside of all viewports
    if (!viewports.length) {
        return;
    }

    // Viewports may overlap. For this case, we need a heuristic metric
    // to choose the best match. For this, we use the
    // distance to the viewport boundary edges.
    const distanceFromEdge = (box, p) => {
        return Math.min(
            Math.min(p.x - box.min.x, box.max.x - p.x),
            Math.min(p.y - box.min.y, box.max.y - p.y)
        );
    };

    // Pick the viewport that minimizes distanceFromEdge
    let getBestMatch = (vp1, vp2) => {
        let d1 = distanceFromEdge(vp1.bounds, point);
        let d2 = distanceFromEdge(vp2.bounds, point);
        return d2 > d1 ? vp2 : vp1;
    };
    return viewports.reduce(getBestMatch).viewport;
};

// Get guid of the level shown by a viewport (may be null, e.g. for SectionViews)
//  @param {Object} vp - viewport object from aecModelData
//  @returns {string|undefined} levelGuid
const getLinkedLevel = (vp) => {

    // Viewports come directly from aecModelData json and do not always reference a level.
    // So, we should not crash if anything is null here.
    const viewRange = vp.extensions && vp.extensions.viewRange;
    const cutPlane  = viewRange && viewRange.cutPlane;
    return cutPlane && cutPlane.levelGuid;
};

// Find all viewports in the document that link the level indicated by levelGuid
//
// @param {Object} aecData   - as obtained from AECModelData.json
// @param {string} levelGuid
// @retuns {Object[]} Array of viewport data objects
const findViewportsForLevel = (aecData, levelGuid) => {

    // Find viewports that links this level
    let viewports = aecData.viewports || [];

    const matchesLevel = (vp) => {
        let vpLevelGuid = getLinkedLevel(vp);
        return vpLevelGuid === levelGuid;
    };
    return viewports.filter(matchesLevel);
};

// Find level in aecModelData that links the given viewport.
//
// @param {Object} aecData - as obtained from AECModelData.json
// @param {Object} viewport object
// @retuns {Object|undefined} level object from aecModelData
const findLevelForViewport = (aecData, viewport) => {
    const levelGuid    = getLinkedLevel(viewport);

    // Search for a level with this levelGuid
    const hasLevelGuid = (level) => (level.guid === levelGuid);
    return levelGuid && aecData.levels.find(hasLevelGuid);
};

// Checks if a viewport provides all required information to compute a 2D/3D transform.
//  @param {Object} vp - vieport data object as obtained from AecModelData.json
const supports2DTo3DTransform = (vp) => {

    // If AECModelData contains a modelToSheetTransform (only possible for models produced using latest RevitAPI changes), we get a matrix directly
    // and don't have to care for sectionBox or isCropBoxActive anymore.
    const canUseTransform = Boolean(vp.modelToSheetTransform);

    // Check if we can use the old code path to reverse-engineer the transform from the SectionBox
    const canUseWorkaround = Boolean(vp.sectionBox && vp.isCropBoxActive);

    // If neither of the two work, we can stop here.
    if (!canUseTransform && !canUseWorkaround) {
        return false;
    }

    const supportedViewportTypes = [...TopViewViewportTypes, 'Section'];

    return Boolean(supportedViewportTypes.includes(vp.viewType) &&
        vp.geometryViewportRegion &&    // We need the viewport outline that corresponds 1:1 with the view sectionBox (excluding labels etc.)
        !vp.hasBreaks &&                // A view in Revit may be split into separate parts using "View breaks". This is not supported yet.
        !vp.extensions.hasRegions);     // The 2D/3D transform may actually vary within a single view. We currently don't get the required data to support this.
};

const AEC_CACHE = {};

// Returns all the fragments that belong to a specific dbId. This only takes the leaf nodes, so we can't
// just call enumNodeFragments with recurse directly.
const getLevelFragmentsIds = (instanceTree, levelDbId) => {
    const leafIds = [];
    const _collectLeafNodeIds = (id) => {
        let childCount = 0;
        instanceTree.enumNodeChildren(id, (childId) => {
            _collectLeafNodeIds(childId);
            childCount += 1;
        });
        if (childCount === 0) {
            leafIds.push(id);
        }
    };
    _collectLeafNodeIds(levelDbId);

    const fragIds = [];
    leafIds.forEach(leafId => {
        instanceTree.enumNodeFragments(leafId, (fragId) => { fragIds.push(fragId); });
    });
    return fragIds;
};

// This function holds heuristics around getting bounding boxes for levels:
// 1. Compute fuzzyBoundingBox to make sure things like pipes coming through multiple floors do not make bounding box too large
// 2. Given that levels are coming in bottom up order, we check that newly computed bounding box does not intersect with the last known level
//    and if it does, reduce the largest box among both levels. This step helps when bounding box is large by nature (e.g. due to high walls spreading over multiple levels)
const getLevelBoundingBox = (dbId, model, lastLevel) => {
    const instanceTree = model.getData().instanceTree;
    if (!instanceTree) {
        return;
    }

    const fragIds = getLevelFragmentsIds(instanceTree, dbId);
    const newBox = model.getFuzzyBox({ allowlist: fragIds });

    // If there is some levels in the storage already, we need to check for intersection
    if (lastLevel.min.z !== Infinity && (lastLevel.max.z > newBox.min.z)) {
        // there is an intersection and some box need to be cropped
        const newBoxHeight = Math.abs(newBox.max.z - newBox.min.z);
        const lastKnownBoxHeight = Math.abs(lastLevel.max.z - lastLevel.min.z);
        if (lastKnownBoxHeight > newBoxHeight) {
            lastLevel.max.z = lastLevel.min.z;
        } else {
            newBox.min.z = lastLevel.max.z;
        }
    }
    lastLevel.copy(newBox);
    return newBox;
};

const computeLevels = (levels, model) => {
    // Need to get and apply opposite to elevation as it will be reapplied elsewhere
    let refPointTf = model.myData.placementWithOffset;
    if (!refPointTf) {
        refPointTf = new THREE.Matrix4();
    }

    const lastLevel = new THREE.Box3();
    levels.forEach((level, idx) => {
        const levelBox = getLevelBoundingBox(level.dbId, model, lastLevel);
        if (levelBox && levelBox.min.z !== Infinity && levelBox.max.z !== Infinity) {
            level.elevation = levelBox.min.z - (refPointTf ? refPointTf.elements[14] : 0);
            if (idx === levels.length - 1) {
                level.height = model.getBoundingBox().max.z - levelBox.min.z;
            } else {
                level.height = levelBox.max.z - levelBox.min.z;
            }
        }
    });

    if (levels.some(l => !l.elevation)) {
        console.warn('Failed to derive elevation for some levels');
        return;
    }

    const refP = [
        refPointTf.elements[0], refPointTf.elements[1], refPointTf.elements[2],
        refPointTf.elements[4], refPointTf.elements[5], refPointTf.elements[6],
        refPointTf.elements[8], refPointTf.elements[9], refPointTf.elements[10],
        refPointTf.elements[12], refPointTf.elements[13], refPointTf.elements[14],
    ];
    const aecModelData = {
        version: '1.0.0', phases: [{ name: 'Default' }], levels, scopeBoxes: [], refPointTransformation: refP,
    };

    return aecModelData;
};

// Couple of queries used here will only work for OTG property database
// In order to make it work for SVF we need to use other property names, coming from Navisworks (Lc*)
const findLevelsOTG = (onDone, onError, model) => {
    const ifcBuildingKey = 'IFCBUILDING';
    model.search(
        ifcBuildingKey,
        dbIds => {
            model.getBulkProperties(
                dbIds, null,
                elements => {
                    // For now, only consider the first IFCBUILDING element (an edge case --maybe 2 buildings within
                    // single IFC file-- ignore the 2nd instance)
                    const exactMatchIndex = elements.findIndex(e => e.properties.find(p => p.displayValue === ifcBuildingKey));
                    if (exactMatchIndex === -1) {
                        return onDone(undefined);
                    }

                    const levelCandidates = elements[exactMatchIndex].properties
                        .filter(e => e.displayName === 'child') // get direct children
                        .map(e => e.displayValue); // get dbId of each child

                    model.getBulkProperties(
                        levelCandidates, ['Name', 'Type'],
                        (levelElements) => {
                            // Levels are stored in Db as they are presented in IFC -> bottom up order, so we must not mess it up
                            const levels = levelElements.map(lev => {
                                const type = lev.properties.find(p => p.displayName === 'Type');
                                const name = lev.properties.find(p => p.displayName === 'Name');
                                // TODO: 'Level' might be localization dependent. 'IfcBuildingStorey' is not, as it is part of the IFC specification.
                                if (type && (type.displayValue.includes('Level') || type.displayValue === 'IfcBuildingStorey') && name) {
                                    return {
                                        guid: lev.dbId.toString(),
                                        name: name.displayValue,
                                        // NOTE: elevation and height will be populated later
                                        dbId: lev.dbId,
                                        extension: {
                                            buildingStory: true,
                                            structure: false,
                                            computationHeight: 0,
                                            groundPlane: false,
                                            hasAssociatedViewPlans: false,
                                        },
                                    };
                                }
                            }).filter(Boolean);

                            onDone(levels);
                        },
                        onError,
                    );
                },
                onError,
            );
        }, onError, ['Type'],
    );
};

const findLevelsSVF = (onDone, onError, model) => {
    model.search(
        'IfcBuildingStorey',
        (dbIds) => {
            model.getBulkProperties(
                dbIds,
                ['Type', 'GUID', 'NAME', 'Name', 'ELEVATION', 'Elevation', 'IfcElevation'],
                elements => {
                    const levels = [];
                    for (let i = 0; i < elements.length; i++) {
                        if (elements[i].properties.length > 1) {
                            const guidIdx = elements[i].properties.findIndex(element => element.displayName === 'GUID');
                            const levelIdx = elements[i].properties.findIndex(element => element.displayValue === 'Levels: Level: Level 1');
                            let nameIdx = -1;
                            let elevationIdx = -1;
                            if (levelIdx >= 0) {
                                nameIdx = elements[i].properties.findIndex(element =>
                                        (element.attributeName.toLowerCase() === 'name' ||
                                            element.attributeName === 'LcOaNode:LcOaSceneBaseUserName') &&
                                        element.displayCategory === 'Item');
                            } else {
                                nameIdx = elements[i].properties.findIndex(element =>
                                        element.displayName.toLowerCase() === 'name' &&
                                        (element.displayCategory === 'IFC' ||
                                            (element.attributeName === 'LcOaNode:LcOaSceneBaseUserName' &&
                                                element.displayCategory === 'Item') ||
                                            (element.attributeName ===
                                                'LcRevitData_Element:LcRevitPropertyElementName' &&
                                                element.displayCategory === 'Element')));
                                elevationIdx = elements[i].properties.findIndex(element =>
                                        (element.displayName === 'ELEVATION' &&
                                            element.displayCategory === 'IFC') ||
                                        ((element.displayName === 'IfcElevation' ||
                                            element.displayName === 'Elevation') &&
                                            element.displayCategory === 'Element'));
                            }
                            if (nameIdx >= 0 && (elevationIdx >= 0 || levelIdx >= 0)) {
                                const guid = guidIdx > -1 ? elements[i].properties[guidIdx].displayValue : elements[i].dbId;

                                levels.push({
                                    guid: guid.toString(),
                                    name: elements[i].properties[nameIdx].displayValue,
                                    dbId: elements[i].dbId,
                                    extension: {
                                        buildingStory: true,
                                        structure: false,
                                        computationHeight: 0,
                                        groundPlane: false,
                                        hasAssociatedViewPlans: false,
                                    },
                                });
                            }
                        }
                    }

                    onDone(levels);
                },
                onError,
            );
        },
        onError, ['Type'],
    );
};

const computeAecModelDataForIfc = (model) => {
    if (!model) {
        return null;
    }

    const cacheKey = model.getModelKey();

    if (AEC_CACHE[cacheKey]) { // We had a heavy computation already, return result from cache
        return AEC_CACHE[cacheKey];
    }

    const promise = new Promise((resolve) => {
        const onDone = (levels) => {
            if (!levels || !levels.length) {
                return resolve(null);
            }

            const aecModelData = computeLevels(levels, model);
            if (cacheKey) {
                AEC_CACHE[cacheKey] = Object.assign({}, aecModelData);
            }
            resolve(aecModelData);
        };

        const onError = (error) => {
            console.error(error);
            resolve(null);
        };

        if (model.isOTG()) {
            findLevelsOTG(onDone, onError, model);
        } else {
            findLevelsSVF(onDone, onError, model);
        }
    });

    if (cacheKey) {
        // Cache the promise so if this is called multiple times it won't start the same computation in parallel
        AEC_CACHE[cacheKey] = promise;
    }

    return promise;
};

const AecModelData = {
    get2DTo3DMatrix,
    get3DTo2DMatrix,
    getViewportBounds,
    remapRectangle,
    findSheetForViewport,
    findViewportsForLevel,
    supports2DTo3DTransform,
    getLinkedLevel,
    findViewportsOnSheet,
    findViewportAtPoint,
    findLevelForViewport,
    computeAecModelDataForIfc,
    TopViewViewportTypes,
    computeLevels,
};

export default AecModelData;

namespace.AecModelData = AecModelData;



